import React, { useEffect, useRef, useState } from 'react';
import './Presentation.css';
import { useLanguage } from '../../context/LanguageContext';
import screenshotPresentation from '../../assets/screenshotPresentation.png';

// Composant pour les particules (similaire à CategoriesParticles)
const PresentationParticles = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Ajuster la taille du canvas à la section
    const resizeCanvas = () => {
      const parentElement = canvas.parentElement;
      if (parentElement) {
        canvas.width = parentElement.offsetWidth;
        canvas.height = parentElement.offsetHeight;
      }
    };
    
    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();
    
    // Configuration des particules
    const particlesArray = [];
    const numberOfParticles = Math.min(window.innerWidth / 8, 150);
    
    const mouse = {
      x: undefined,
      y: undefined,
      radius: 100
    };
    
    // Capturer la position de la souris
    const handleMouseMove = (event) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = event.clientX - rect.left;
      mouse.y = event.clientY - rect.top;
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    
    class Particle {
      constructor() {
        // Position sur tout l'écran sans restriction
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        
        // Taille variable pour plus de dynamisme
        this.size = Math.random() * 2.5 + 0.8;
        this.baseSize = this.size;
        
        // Vitesse et direction
        this.speedX = (Math.random() * 1 - 0.5) * 0.6;
        this.speedY = (Math.random() * 1 - 0.5) * 0.6;
        this.baseSpeedX = this.speedX;
        this.baseSpeedY = this.speedY;
        
        // Animation et apparence - couleur magenta comme dans l'image
        this.pulsing = Math.random() * 0.03 + 0.01;
        this.colorAngle = Math.random() * 360;
        this.colorSpeed = Math.random() * 0.3 + 0.1;
        this.alpha = Math.random() * 0.4 + 0.3;
        this.glow = Math.random() * 6 + 2;
        this.hueOffset = Math.random() * 20 - 10;
      }
      
      update() {
        // Mouvement de base
        this.x += this.speedX;
        this.y += this.speedY;
        
        // Animation pulsante et changement de couleur
        this.colorAngle += this.colorSpeed;
        if (this.colorAngle > 360) this.colorAngle = 0;
        
        // Effet respirant
        this.size = this.baseSize + Math.sin(Date.now() * this.pulsing) * (this.baseSize * 0.3);
        
        // Interaction avec la souris
        if (mouse.x) {
          const mdx = mouse.x - this.x;
          const mdy = mouse.y - this.y;
          const mouseDistance = Math.sqrt(mdx * mdx + mdy * mdy);
          
          if (mouseDistance < mouse.radius) {
            const angle = Math.atan2(mdy, mdx);
            const force = (mouse.radius - mouseDistance) / mouse.radius;
            
            // Effet de répulsion
            this.speedX -= Math.cos(angle) * force * 0.2;
            this.speedY -= Math.sin(angle) * force * 0.2;
            
            // Augmenter l'opacité et la taille à l'approche de la souris
            this.alpha = Math.min(this.alpha + 0.2, 0.9);
            this.size = this.baseSize * (1 + force * 0.9);
          } else {
            // Revenir progressivement à la vitesse et opacité de base
            this.speedX = this.speedX * 0.97 + this.baseSpeedX * 0.03;
            this.speedY = this.speedY * 0.97 + this.baseSpeedY * 0.03;
            this.alpha = Math.max(this.alpha * 0.98, Math.random() * 0.4 + 0.3);
            this.size = Math.max(this.size * 0.98, this.baseSize);
          }
        }
        
        // Limiter la vitesse
        const maxSpeed = 1.5;
        const currentSpeed = Math.sqrt(this.speedX * this.speedX + this.speedY * this.speedY);
        if (currentSpeed > maxSpeed) {
          this.speedX = (this.speedX / currentSpeed) * maxSpeed;
          this.speedY = (this.speedY / currentSpeed) * maxSpeed;
        }
        
        // Friction légère
        this.speedX *= 0.99;
        this.speedY *= 0.99;
        
        // Rebond aux limites de l'écran
        const margin = 10; // Marge pour éviter que les particules ne disparaissent complètement
        
        if (this.x < margin) {
          this.x = margin;
          this.speedX *= -0.8;
        } else if (this.x > canvas.width - margin) {
          this.x = canvas.width - margin;
          this.speedX *= -0.8;
        }
        
        if (this.y < margin) {
          this.y = margin;
          this.speedY *= -0.8;
        } else if (this.y > canvas.height - margin) {
          this.y = canvas.height - margin;
          this.speedY *= -0.8;
        }
      }
      
      draw() {
        // Centre plus défini avec moins de halo
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size * 0.7, 0, Math.PI * 2);
        
        // Couleur magenta comme sur l'image
        const hue = Math.abs(Math.sin(this.colorAngle) * 30) + 320 + this.hueOffset;
        const saturation = 100;
        const lightness = 68 + Math.sin(this.colorAngle * 0.5) * 8;
        
        // Centre plus opaque pour mieux définir la particule
        ctx.fillStyle = `hsla(${hue}, ${saturation}%, ${lightness}%, ${this.alpha * 1.2})`;
        ctx.fill();
        
        // Effet de halo lumineux
        const gradient = ctx.createRadialGradient(
          this.x, this.y, 0,
          this.x, this.y, this.size + this.glow
        );
        
        gradient.addColorStop(0, `hsla(${hue}, ${saturation}%, ${lightness + 10}%, ${this.alpha * 0.9})`);
        gradient.addColorStop(0.3, `hsla(${hue}, ${saturation}%, ${lightness}%, ${this.alpha * 0.6})`);
        gradient.addColorStop(0.6, `hsla(${hue}, ${saturation}%, ${lightness - 10}%, ${this.alpha * 0.2})`);
        gradient.addColorStop(1, `hsla(${hue}, ${saturation}%, ${lightness - 20}%, 0)`);
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size + this.glow, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    
    // Créer les particules
    const init = () => {
      particlesArray.length = 0;
      for (let i = 0; i < numberOfParticles; i++) {
        particlesArray.push(new Particle());
      }
    };
    init();
    
    // Animer les particules
    let animationFrameId;
    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particlesArray.forEach(particle => {
        particle.update();
        particle.draw();
      });
      
      animationFrameId = requestAnimationFrame(animate);
    }
    animate();
    
    // Nettoyage
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);
  
  return (
    <canvas ref={canvasRef} className="presentation-particles" />
  );
};

// Composant pour un point de fonctionnalité avec puce verte et symbole de validation
const FeaturePoint = ({ text, delay }) => {
  const [visible, setVisible] = useState(false);
  const featureRef = useRef(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setTimeout(() => {
            setVisible(true);
          }, delay);
        }
      },
      { threshold: 0.3 }
    );
    
    if (featureRef.current) {
      observer.observe(featureRef.current);
    }
    
    return () => {
      if (featureRef.current) {
        observer.unobserve(featureRef.current);
      }
    };
  }, [delay]);
  
  const featureStyle = {
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(10px)',
    transition: 'opacity 0.5s, transform 0.5s'
  };
  
  return (
    <div className="feature-point" style={featureStyle} ref={featureRef}>
      <span className="feature-dot"></span>
      <span className="feature-text">{text}</span>
    </div>
  );
};

function Presentation() {
  const { t } = useLanguage();
  const [animateImage, setAnimateImage] = useState(false);
  const [animateContent, setAnimateContent] = useState(false);
  
  useEffect(() => {
    const imageTimer = setTimeout(() => {
      setAnimateImage(true);
    }, 300);
    
    const contentTimer = setTimeout(() => {
      setAnimateContent(true);
    }, 500);
    
    return () => {
      clearTimeout(imageTimer);
      clearTimeout(contentTimer);
    };
  }, []);
  
  const contentStyle = {
    opacity: animateContent ? 1 : 0,
    transform: animateContent ? 'translateY(0)' : 'translateY(20px)',
    transition: 'opacity 0.8s ease, transform 0.8s ease'
  };
  
  return (
    <section className="presentation-section" id="presentation">
      <PresentationParticles />
      <div className="container">
        <div className="presentation-content">
          <div className="presentation-image-container">
            <div 
              className={`presentation-image ${animateImage ? 'animate' : ''}`}
              style={{ backgroundImage: `url(${screenshotPresentation})` }}
            >
            </div>
          </div>
          
          <div className="presentation-text" style={contentStyle}>
            <div className="text-content-wrapper">
              <h2 className="presentation-title">{t('interfaceIntuitive')}</h2>
              <div className="description-block">
                <p className="presentation-description">
                  {t('presentationDescription')}
                </p>
                <p className="presentation-subtitle">
                  {t('presentationSubtitle')}
                </p>
              </div>
              
              <h3 className="presentation-master">{t('presentationMaster')}</h3>
              
              <div className="feature-columns">
                <div className="feature-column">
                  <FeaturePoint text={t('featureSearch')} delay={100} />
                  <FeaturePoint text={t('featureRank')} delay={300} />
                  <FeaturePoint text={t('featureProgress')} delay={500} />
                </div>
                <div className="feature-column">
                  <FeaturePoint text={t('featurePersonalize')} delay={200} />
                  <FeaturePoint text={t('featureMode')} delay={400} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Presentation; 